import { IAccountTag, IImpersonationContext, ITagCategory } from '@ViewModels';
import { FC, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { useDebounceCallback } from '../../../../../hooks/useDebounceValue';
import { useErrorMessages } from '../../../../../models/hooks/appStateHooks';
import { invalidateAllTagFilterQueries, useTagsQuery } from '../../../../../queries';
import { usePutTagCategoryMutation } from '../../../../../queries/Tag/usePutTagCategoryMutation';
import { useAddTagsToCategoryMutation } from '../../../../../queries/TagCategory/useAddTagToCategoryMutation';
import { invalidateTagCategoryQuery } from '../../../../../queries/TagCategory/useGetTagCategoryQuery';
import { useCategorySync } from '../useCategorySync';
import { Column, Row } from '../../../../components-v2/PageLayout/Layout';
import { Pill } from '../../../../components-v2/Pill/Pill';
import { PillListBuilder } from '../../../../components-v2/PillListBuilder/PillListBuilder';
import { Button, ButtonBase } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { XIcon } from '../../../../components/svgs/icons/XIcon';
import { useTagsDataContext } from '../TagsContexts/TagsDataContext';
import { styleSheet } from './styles';

interface UpdateCategoryModal {
	isOpen: boolean;
	onClose: () => void;
	impersonationContext: IImpersonationContext;
}

export const UpdateCategoryModal: FC<UpdateCategoryModal> = ({ isOpen, onClose, impersonationContext }) => {
	const errorMessages = useErrorMessages();
	const { categories, selectedTag, setSelectedTag, selectedCategory } = useTagsDataContext();
	const { getCategorySync } = useCategorySync(impersonationContext);
	const [category, setCategory] = useState<ITagCategory[]>([]);
	const [tagsToAdd, setTagsToAdd] = useState<IAccountTag[]>([]);

	const [searchTerm, setSearchTerm] = useState<string>(null);
	const debouncedSetSearchTerm = useDebounceCallback(setSearchTerm);

	const handleClose = () => {
		if (selectedTag) {
			setSelectedTag(null);
		}
		if (category) {
			setCategory(null);
		}
		if (tagsToAdd?.length) {
			setTagsToAdd([]);
		}
		onClose();
	};

	const tagsQuery = useTagsQuery({
		enabled: !selectedTag,
		impersonationContext,
		query: searchTerm,
	});

	const tags = useMemo(() => {
		const value = tagsQuery.data?.pages?.flatMap(page => page.values);
		return value ?? [];
	}, [tagsQuery.data?.pages]);

	const putTagCategory = usePutTagCategoryMutation({
		onSuccess: () => {
			toast.success('Tag updated.');
			invalidateAllTagFilterQueries();
			handleClose();
		},
		onError: error => {
			errorMessages.pushApiError(error);
			toast.error(`An error occured. Tag could not be updated.`);
			handleClose();
		},
	});

	const addTagsToCategory = useAddTagsToCategoryMutation({
		onSuccess: () => {
			toast.success('Category updated.');
			invalidateTagCategoryQuery({ categoryId: selectedCategory.id });
			handleClose();
		},
		onError: error => {
			errorMessages.pushApiError(error);
			toast.error(`An error occured. Category could not be updated.`);
			handleClose();
		},
	});

	const handleCreateNewTag = (newTag: IAccountTag) => {
		if (selectedCategory) {
			// Add the new tag to the list of tags to be added to the category
			setTagsToAdd(prev => [...prev, newTag]);
		}
	};

	const handleSave = () => {
		// ListBuilder multiselect is turned off so there will only ever be 1 item
		if (selectedTag) {
			putTagCategory.mutate({ categoryId: category?.[0].id, tagId: selectedTag.id, impersonationContext });
		}
		if (selectedCategory) {
			addTagsToCategory.mutate({ categoryId: selectedCategory.id, tags: tagsToAdd, impersonationContext });
		}
	};

	return (
		<Modal isOpen={isOpen} onClose={handleClose}>
			<Row styles={[styleSheet.headerRow]}>
				<ButtonBase onClick={handleClose}>
					<XIcon height={14} width={14} />
				</ButtonBase>
			</Row>
			<Column styles={[styleSheet.formContainer]}>
				<Row styles={[styleSheet.titleRow]}>
					<span>Add Tag to Category</span>
				</Row>
				<Row styles={[styleSheet.formRow]}>
					{selectedTag ? (
						<Pill variant='tag' label={selectedTag?.name} color={selectedTag?.color} />
					) : (
						<Pill variant='category' label={selectedCategory?.name} color={selectedCategory?.color} />
					)}
				</Row>
				<Row styles={[styleSheet.formRow]}>
					<Column fullWidth>
						<PillListBuilder
							displaySelected
							setSearchTerm={debouncedSetSearchTerm}
							fetchMoreOptions={selectedTag ? categories.fetchNextPage : tagsQuery.fetchNextPage}
							hasMoreOptions={selectedTag ? categories.hasNextPage : tagsQuery.hasNextPage}
							isLoading={selectedTag ? categories.isFetching : tagsQuery.isPending}
							options={
								selectedTag
									? (categories?.data ?? []).filter(option => !getCategorySync(option).isLocked)
									: // A tag in a synced category can't leave it; one in a category not loaded yet is left to the API
										(tags ?? []).filter(
											option =>
												!getCategorySync(categories?.data?.find(loaded => loaded.id === option.category?.id)).isLocked
										)
							}
							onOptionClick={selectedTag ? setCategory : setTagsToAdd}
							selectedOptions={selectedTag ? category : tagsToAdd}
							variant={selectedTag ? 'category' : 'tag'}
							multiSelect={selectedTag ? false : true}
							onEditOptions={selectedCategory ? handleCreateNewTag : undefined}
						/>
					</Column>
				</Row>

				<Row styles={[styleSheet.buttonRow]}>
					<Button kind='primary' label='Save' onClick={handleSave} />
					<Button kind='reverse' label='Cancel' onClick={handleClose} />
				</Row>
			</Column>
		</Modal>
	);
};
